import {
  UI_SYNTHETIC_APPLICATION_STATUS_ORDER_NO_DEPRECATED,
  UI_SYNTHETIC_FRACTION_EXCLUSIVE_APPLICATION_STATUS_ORDER_NO_DEPRECATED,
  UI_SYNTHETIC_WHITELABEL_APPLICATION_STATUS_ORDER_NO_DEPRECATED,
  enums,
} from "@fraction/shared";
import _ from "lodash";
import { useMemo, useState } from "react";
import fraction from "src/api/fraction";
import { ConveyancerSelect } from "src/apps/LOS/components/ConveyancerSelect";
import { LenderSelect } from "src/apps/LOS/components/LenderSelect";
import { ProvinceSelect } from "src/apps/LOS/components/ProvinceSelect";
import { useChecklistModal } from "src/components/AppChecklistModal";
import { AppsPipelineView } from "src/components/AppsPipelineView";
import { useInfiniteApplicationsQuery } from "src/hooks/useApplication";
import useToggler from "src/hooks/useToggler";
import { useQuery } from "src/lib";
import { SalesPersonSelect } from "../components/SalesPersonSelect";

export function Pipeline() {
  const {
    data: apps,
    count,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useInfiniteApplicationsQuery({
    status: "active",
    accountType: "employee",
  });

  const { data: salesOptions } = useQuery({
    queryKey: ["salesOptions"],
    queryFn: () =>
      fraction.getEmployees([enums.InternalEmployeeType.BDM, enums.InternalEmployeeType.INSIDE_SALES]),
  });

  const { Toggle: SortByCloseDateToggle, on: sortByCloseDate } = useToggler({
    defaultValue: true,
    id: "sort-by-close-date",
    label: "Sort by close date",
    direction: "left",
  });
  const [provinceFilter, setProvinceFilter] = useState<string | null>(null);
  const [conveyancerFilter, setConveyancerFilter] = useState<enums.ConveyancerGroup | null>(null);
  const [lenderFilter, setLenderFilter] = useState<string | null>("all");
  const [salesPersonFilter, setSalesPersonFilter] = useState<string | null>(null);

  const filteredApps = useMemo(
    () =>
      apps?.filter((app) => {
        if (!provinceFilter && !conveyancerFilter && !lenderFilter) {
          return app;
        }

        let matches = true;

        if (provinceFilter) {
          matches = matches && app.property?.administrativeArea === provinceFilter;
        }
        if (conveyancerFilter) {
          matches = matches && app.conveyancerGroupId === conveyancerFilter;
        }
        if (lenderFilter && lenderFilter !== "all") {
          if (lenderFilter === "others") {
            matches = matches && app.lenderId !== enums.LenderName.FRACTION;
          } else {
            matches = matches && app.lenderId === lenderFilter;
          }
        }
        if (salesPersonFilter) {
          if (salesPersonFilter.startsWith("region:")) {
            const region = salesPersonFilter.split(":")[1];
            matches =
              matches &&
              !!app.internalEmployees?.find(
                (e) =>
                  e.licensedRegions?.includes(region as enums.Region) &&
                  e.type === enums.InternalEmployeeType.BDM
              );
          } else if (salesPersonFilter === "all") {
            // do nothing
          } else {
            matches = matches && !!app.internalEmployees?.find((e) => e.id === salesPersonFilter);
          }
        }

        return matches;
      }),
    [provinceFilter, conveyancerFilter, lenderFilter, apps, salesPersonFilter]
  );
  const sortedApps = useMemo(
    () => (sortByCloseDate ? _.sortBy(filteredApps, "estimatedCloseDate") : filteredApps),
    [filteredApps, sortByCloseDate]
  );
  const { setShowModal, ChecklistModal } = useChecklistModal();

  return (
    <AppsPipelineView
      preference="employee"
      salesOptions={salesOptions}
      showSalesPerson
      showLenderName
      apps={sortedApps}
      isFetching={isFetching}
      handleRefetchClick={refetch}
      totalCount={count}
      showFees
      dataUpdatedAt={dataUpdatedAt}
      visibleStages={
        lenderFilter === "all" || !lenderFilter
          ? UI_SYNTHETIC_APPLICATION_STATUS_ORDER_NO_DEPRECATED
          : lenderFilter === enums.LenderName.FRACTION
          ? UI_SYNTHETIC_FRACTION_EXCLUSIVE_APPLICATION_STATUS_ORDER_NO_DEPRECATED
          : UI_SYNTHETIC_WHITELABEL_APPLICATION_STATUS_ORDER_NO_DEPRECATED
      }
      setShowModal={setShowModal}
      groupBy="syntheticStatus"
      modal={<ChecklistModal showRetoolButton />}
      header={
        <div className="flex flex-row gap-x-6 gap-y-4 flex-wrap mb-2">
          <SortByCloseDateToggle className="w-[200px]" />
          <div className="flex flex-row gap-x-6 gap-y-2 flex-wrap">
            <ProvinceSelect
              className="w-[300px]"
              onValueChange={setProvinceFilter}
              value={provinceFilter || undefined}
            />
            <ConveyancerSelect
              className="w-[300px]"
              value={conveyancerFilter || undefined}
              onValueChange={setConveyancerFilter}
            />
            <LenderSelect
              className="w-[300px]"
              value={lenderFilter || undefined}
              onValueChange={setLenderFilter}
            />
            <SalesPersonSelect
              className="w-[300px]"
              value={salesPersonFilter || undefined}
              onValueChange={setSalesPersonFilter}
              employees={salesOptions}
            />
          </div>
        </div>
      }
    />
  );
}
